import { Box } from '@material-ui/core';
import {
  CreditCardBorrowerDocumentationRequired,
  DocumentationRequired,
  getAllDocumentationRequiredByLoanId,
  patchSetAreDocumentsConfirmed,
} from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { AddRequiredFileDialog } from '@/components/modules/Documentation/components/DocumentationRequiredHeader/components/AddRequiredFileDialog';
import { useAddRequiredFile } from '@/components/modules/Documentation/components/DocumentationRequiredHeader/hooks/useAddRequiredFile';
import { useDocumentationRequiredHeaderActions } from '@/components/modules/Documentation/components/DocumentationRequiredHeader/hooks/useDocumentationRequiredHeaderActions';
import { AddRequiredFileToDocumentationRequiredType } from '@/components/modules/Documentation/components/DocumentationRequiredHeader/schemas/AddRequiredFileToDocumentationRequiredSchema';
import { DocumentationRequiredAction } from '@/components/modules/Documentation/components/DocumentationRequiredHeader/types/DocumentationRequiredAction';
import { DocumentationRequiredProgressBar } from '@/components/modules/Documentation/components/shared/DocumentationRequiredProgressBar';
import { useDocumentationRequired } from '@/components/modules/Documentation/hooks/useDocumentationRequired';
import { DocumentationCardParams } from '@/components/modules/Documentation/types/DocumentationCardParams';
import { DocumentationMode } from '@/components/modules/Documentation/types/DocumentationMode';
import { DocumentManagerStandardActionDialogParams } from '@/components/modules/Documentation/types/DocumentManagerStandardActionDialogParams';
import { Font } from '@/components/shared/components/Font';
import { IconHeader } from '@/components/shared/components/IconHeader';
import { MoreActionsMenuWithIcon } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenuWithIcon';
import { StandardActionDialog } from '@/components/shared/components/StandardActionDialog/StandardActionDialog';
import { Text } from '@/components/shared/components/Text';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';

interface DocumentationRequiredHeaderProps {
  loanId: string;
  documentationCardParams: DocumentationCardParams;
  documentationRequired: DocumentationRequired | CreditCardBorrowerDocumentationRequired;
  mode: DocumentationMode;
}

export const DocumentationRequiredHeader = ({
  loanId,
  documentationCardParams,
  documentationRequired,
  mode,
}: DocumentationRequiredHeaderProps) => {
  const { opened, setOpened, toggleDialog } = useDialog();
  const { request, loading: requestLoading } = useRequest();
  const { revalidate } = useCache(
    getAllDocumentationRequiredByLoanId,
    { loanId: loanId },
    { revalidateOnMount: false },
  );

  const {
    uploadedProgress,
    sendDocumentationIncidenceNotification,
    loading: sendingNotification,
  } = useDocumentationRequired({
    documentationRequired,
    loanId: loanId,
    setOpened,
  });

  const {
    addRequiredFileToDocumentationRequired,
    addRequiredFileLoading,
    addRequiredFiles,
    setAddRequiredFiles,
    resetAddRequiredFileParams,
  } = useAddRequiredFile();

  const [actionDialogParams, setActionDialogParams] = useState<DocumentManagerStandardActionDialogParams | undefined>();

  const documentationRequiredType = documentationCardParams.documentationRequiredType;

  const getActionDialogParamsByType = (
    actionType: DocumentationRequiredAction,
  ): DocumentManagerStandardActionDialogParams | undefined => {
    switch (actionType) {
      case DocumentationRequiredAction.SET_ARE_DOCUMENTS_CONFIRMED:
        return {
          actionType: DocumentationRequiredAction.SET_ARE_DOCUMENTS_CONFIRMED,
          titleTextElement: <Text tId={'SET_ARE_DOCUMENTS_CONFIRMED'} />,
          actionDescriptionTextElement: <Text tId={'SET_ARE_DOCUMENTS_CONFIRMED_DESCRIPTION'} />,
          actionButtonsArray: [
            {
              onClick: async () => {
                await request(patchSetAreDocumentsConfirmed, {
                  id: documentationRequired.id,
                  documentationRequiredType,
                  areDocumentsConfirmed: true,
                });
                setOpened(false);
                revalidate();
              },
              buttonLabelTextId: 'CONFIRM',
            },
          ],
        };
      case DocumentationRequiredAction.SEND_INCIDENCE_NOTIFICATION:
        return {
          actionType: DocumentationRequiredAction.SEND_INCIDENCE_NOTIFICATION,
          titleTextElement: <Text tId={'SEND_INCIDENCE_NOTIFICATION'} />,
          actionDescriptionTextElement: <Text tId={'SEND_INCIDENCE_NOTIFICATION_DESCRIPTION'} />,
          actionButtonsArray: [
            {
              onClick: sendDocumentationIncidenceNotification,
              buttonLabelTextId: 'SEND_EMAIL',
            },
          ],
        };
      default:
        break;
    }
  };

  const openActionDialogByActionType = (actionType: DocumentationRequiredAction) => {
    if (actionType === DocumentationRequiredAction.ADD_REQUIRED_FILE_TYPE_TO_DOCUMENTATION_REQUIRED) {
      setAddRequiredFiles(true);
      toggleDialog();
      return;
    }
    const actionDialogParams = getActionDialogParamsByType(actionType);
    setActionDialogParams(actionDialogParams);
    toggleDialog();
  };

  const { getActionOptionsArray } = useDocumentationRequiredHeaderActions({
    onClickAction: openActionDialogByActionType,
  });

  const actionOptionsArray = getActionOptionsArray(mode);

  const actionsAllowed = mode === DocumentationMode.FULL && actionOptionsArray.length > 0;

  return (
    <>
      <Box width={'100%'} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
        <Box width={'65%'} display={'flex'} alignItems={'center'}>
          <IconHeader iconName={documentationCardParams.iconName} color={'secondary'} />
          <Box display={'flex'} flexDirection={'column'} overflow={'hidden'}>
            <Font variant={'overline'} color={'textSecondary'}>
              {documentationCardParams.labelText}
            </Font>
            <Box className={'overflow-text-ellipsis'}>
              <Font color={'title'} variant={'body2'}>
                <strong>{documentationCardParams.labelIdentifier}</strong>
              </Font>
            </Box>
          </Box>
        </Box>
        <Box width={'35%'} display={'flex'} alignItems={'center'}>
          <Box ml={2} flexGrow={1}>
            <DocumentationRequiredProgressBar uploadedProgress={uploadedProgress} />
          </Box>
          {actionsAllowed && (
            <Box ml={1}>
              <MoreActionsMenuWithIcon actionsOptionsArray={actionOptionsArray} />
            </Box>
          )}
        </Box>
      </Box>
      {opened &&
        (actionDialogParams?.actionType === DocumentationRequiredAction.SET_ARE_DOCUMENTS_CONFIRMED ||
          actionDialogParams?.actionType === DocumentationRequiredAction.SEND_INCIDENCE_NOTIFICATION) && (
          <StandardActionDialog
            opened={opened}
            setOpened={setOpened}
            setActionDialogParams={setActionDialogParams}
            titleTextElement={actionDialogParams.titleTextElement}
            actionDescriptionTextElement={actionDialogParams.actionDescriptionTextElement}
            actionButtonsArray={actionDialogParams.actionButtonsArray}
            loading={requestLoading || sendingNotification}
          />
        )}
      {opened && addRequiredFiles && (
        <AddRequiredFileDialog
          opened={opened}
          setOpened={setOpened}
          handleClose={() => {
            resetAddRequiredFileParams();
            setOpened(false);
            setActionDialogParams(undefined);
          }}
          requiredFiles={documentationRequired.requiredFiles}
          selectedDocumentationRequiredType={documentationRequiredType}
          onSubmitForm={async (values: AddRequiredFileToDocumentationRequiredType) => {
            if (!values.fileTypeId) return;

            await addRequiredFileToDocumentationRequired({
              loanId: loanId,
              documentationRequired,
              documentationRequiredType,
              fileTypeId: values.fileTypeId,
            });
            setOpened(false);
            revalidate();
          }}
          loading={addRequiredFileLoading}
        />
      )}
    </>
  );
};
